/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import { SkeletonText, SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { DatacenterConnection } from "../DatacenterConnectionsData";

export interface DatacenterConnectionsViewModalProps {
  isOpen: boolean;
  isLoading: boolean;
  connection: DatacenterConnection | null;
  onClose: () => void;
}

export const DatacenterConnectionsViewModal: React.FC<DatacenterConnectionsViewModalProps> = ({
  isOpen,
  isLoading,
  connection,
  onClose,
}) => {
  const [activeSourceTab, setActiveSourceTab] = useState<"datacenter" | "hardware" | "port">("datacenter");
  const [activeDestTab, setActiveDestTab] = useState<"datacenter" | "hardware" | "port">("datacenter");

  const hasSourceData = connection && (
    connection.datacenterSource || 
    connection.hardwareSource || 
    connection.portSource
  );

  const hasDestData = connection && (
    connection.datacenterDest || 
    connection.hardwareDest || 
    connection.portDest
  );

  const SOURCE_TABS = [
    { id: "datacenter", label: "Datacenter" },
    { id: "hardware", label: "Hardware" },
    { id: "port", label: "Port" },
  ];

  const DEST_TABS = [
    { id: "datacenter", label: "Datacenter" },
    { id: "hardware", label: "Hardware" },
    { id: "port", label: "Port" },
  ];

  return (
    <GenericModal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="View Datacenter Connection Details"
      customWidth="max-w-7xl"
    >
      {isLoading ? (
        <div className="space-y-6">
          <div>
            <SkeletonBlock h={20} w="30%" className="mb-3" />
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 9 }).map((_, idx) => (
                <SkeletonText key={idx} lines={2} lineHeight={14} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        connection && (
          <div className="space-y-6">
            {/* Connection Information */}
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Connection Information
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <GenericInput
                  label="ID"
                  value={String(connection.id || "N/A")}
                  readOnly
                />
                <GenericInput
                  label="Subscription ID"
                  value={connection.subId || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Connection Type"
                  value={connection.connectionType || "N/A"}
                  readOnly
                />
                <GenericInput
                  label="Capacity (Mbps)"
                  value={String(connection.capacityMbps || "N/A")}
                  readOnly
                />
              </div>
            </GenericCard>

            {/* Source Information */}
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Source Information
              </h3>
              
              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="flex space-x-4" aria-label="Tabs">
                  {SOURCE_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveSourceTab(tab.id as "datacenter" | "hardware" | "port")}
                      className={`
                        px-3 py-2 text-sm font-medium focus:outline-none rounded-t
                        ${
                          activeSourceTab === tab.id
                            ? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary bg-semantic-bg-secondary"
                            : "text-gray-500 hover:text-gray-700"
                        }
                      `}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              {!hasSourceData ? (
                <div className="text-center py-8 text-gray-500">
                  No data available
                </div>
              ) : (
                <div className="mt-4">
                  {/* Datacenter Tab */}
                  {activeSourceTab === "datacenter" && (
                    connection.datacenterSource ? (
                      <div className="grid grid-cols-3 gap-4">
                        <GenericInput
                          label="Datacenter ID"
                          value={connection.datacenterSource.datacenterId || "N/A"}
                          readOnly
                        />
                        <GenericInput
                          label="Display Name"
                          value={connection.datacenterSource.displayName || "N/A"}
                          readOnly
                        />
                        <GenericInput
                          label="Direction"
                          value={connection.datacenterSource.direction || "N/A"}
                          readOnly
                        />
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No datacenter data available
                      </div>
                    )
                  )}

                  {/* Hardware Tab */}
                  {activeSourceTab === "hardware" && (
                    connection.hardwareSource ? (
                      <div className="grid grid-cols-3 gap-4">
                        <GenericInput
                          label="Hostname"
                          value={connection.hardwareSource.hostname || "N/A"}
                          readOnly
                        />
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No hardware data available
                      </div>
                    )
                  )}

                  {/* Port Tab */}
                  {activeSourceTab === "port" && (
                    connection.portSource ? (
                      <div className="grid grid-cols-3 gap-4">
                        <GenericInput
                          label="Port Number"
                          value={connection.portSource.portNumber || "N/A"}
                          readOnly
                        />
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No port data available
                      </div>
                    )
                  )}
                </div>
              )}
            </GenericCard>

            {/* Destination Information */}
            <GenericCard isCollapsible={false} className="p-5">
              <h3 className="text-md font-semibold mb-4 text-semantic-text-primary">
                Destination Information
              </h3>
              
              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="flex space-x-4" aria-label="Tabs">
                  {DEST_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveDestTab(tab.id as "datacenter" | "hardware" | "port")}
                      className={`
                        px-3 py-2 text-sm font-medium focus:outline-none rounded-t
                        ${
                          activeDestTab === tab.id
                            ? "text-semantic-text-brand border-b-2 border-semantic-border-brand-primary bg-semantic-bg-secondary"
                            : "text-gray-500 hover:text-gray-700"
                        }
                      `}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              {!hasDestData ? (
                <div className="text-center py-8 text-gray-500">
                  No data available
                </div>
              ) : (
                <div className="mt-4">
                  {/* Datacenter Tab */}
                  {activeDestTab === "datacenter" && (
                    connection.datacenterDest ? (
                      <div className="grid grid-cols-3 gap-4">
                        <GenericInput
                          label="Datacenter ID"
                          value={connection.datacenterDest.datacenterId || "N/A"}
                          readOnly
                        />
                        <GenericInput
                          label="Display Name"
                          value={connection.datacenterDest.displayName || "N/A"}
                          readOnly
                        />
                        <GenericInput
                          label="Direction"
                          value={connection.datacenterDest.direction || "N/A"}
                          readOnly
                        />
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No datacenter data available
                      </div>
                    )
                  )}

                  {/* Hardware Tab */}
                  {activeDestTab === "hardware" && (
                    connection.hardwareDest ? (
                      <div className="grid grid-cols-3 gap-4">
                        <GenericInput
                          label="Hostname"
                          value={connection.hardwareDest.hostname || "N/A"}
                          readOnly
                        />
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No hardware data available
                      </div>
                    )
                  )}

                  {/* Port Tab */}
                  {activeDestTab === "port" && (
                    connection.portDest ? (
                      <div className="grid grid-cols-3 gap-4">
                        <GenericInput
                          label="Port Number"
                          value={connection.portDest.portNumber || "N/A"}
                          readOnly
                        />
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        No port data available
                      </div>
                    )
                  )}
                </div>
              )}
            </GenericCard>
          </div>
        )
      )}

      <div className="flex justify-end mt-6">
        <GenericButton label="Close" variant="secondary" onClick={onClose} />
      </div>
    </GenericModal>
  );
};
